import React from "react";
import { Paper, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { Bulletin } from "../../Blacklist/types";
import StickyTable from "../../shared/StickyTable";
import { BlacklistSearchResponse } from "./types";

interface IProps {
  data: BlacklistSearchResponse[];
}

interface FlatRow {
  type: string;
  score: number;
  recordName: string;
  taxID: string;
  notes: string;
  bulletins: Bulletin[];
}

function flattenResponses(data: BlacklistSearchResponse[]): FlatRow[] {
  return data.map((response) => {
    let recordName = "";
    let taxID = "";
    let notes = "";
    let bulletins: Bulletin[] = [];

    if (response.personResult) {
      const p = response.personResult;
      recordName = [p.firstName, p.middleName, p.lastName].filter(Boolean).join(" ");
      taxID = p.taxID ?? "";
      notes = p.notes ?? "";
      bulletins = (p.bulletins ?? []).map((b) => b.bulletin);
    } else if (response.companyResult) {
      const c = response.companyResult;
      recordName = c.name;
      taxID = c.taxID ?? "";
      notes = c.notes ?? "";
      bulletins = (c.bulletins ?? []).map((b) => b.bulletin);
    }

    return { type: response.type, score: response.score, recordName, taxID, notes, bulletins };
  });
}

function BulletinCell({ bulletins }: { bulletins: Bulletin[] }) {
  if (bulletins.length === 0) return null;
  return (
    <>
      {bulletins.map((b, i) => (
        <div
          key={b.id ?? i}
          style={{ marginBottom: i < bulletins.length - 1 ? 8 : 0, paddingBottom: i < bulletins.length - 1 ? 8 : 0, borderBottom: i < bulletins.length - 1 ? "1px solid #eee" : undefined }}
        >
          <strong>#{b.number}</strong>
          {b.underwriter?.name && <> &mdash; {b.underwriter.name}</>}
          {b.date && <> &mdash; {new Date(b.date).toLocaleDateString()}</>}
          {b.locations && b.locations.length > 0 && (
            <> &mdash; {b.locations.map((loc) => [loc.county?.name, loc.state?.name].filter(Boolean).join(", ")).join("; ")}</>
          )}
          {b.url && (
            <a
              href={`/api/blacklist/openBulletin?id=${b.url}`}
              target="_blank"
              rel="noreferrer"
              style={{ marginLeft: 8 }}
            >
              View
            </a>
          )}
        </div>
      ))}
    </>
  );
}

export default function NewResultList({ data }: IProps) {
  const rows = flattenResponses(data);

  return (
    <TableContainer component={Paper}>
      <StickyTable>
        <TableHead>
          <TableRow>
            <TableCell>Type</TableCell>
            <TableCell>Score</TableCell>
            <TableCell>Blacklist Name</TableCell>
            <TableCell>Tax ID</TableCell>
            <TableCell>Notes</TableCell>
            <TableCell>Bulletins</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={i}>
              <TableCell>{row.type}</TableCell>
              <TableCell>{row.score}</TableCell>
              <TableCell>{row.recordName}</TableCell>
              <TableCell>{row.taxID}</TableCell>
              <TableCell>{row.notes}</TableCell>
              <TableCell>
                <BulletinCell bulletins={row.bulletins} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </StickyTable>
    </TableContainer>
  );
}
